import {
  getAlertHeadline,
  getAlertIdentifier,
  getAlertSeverity,
  getAlertWeatherZoneIds,
  getHighestSeverityAlert,
} from 'features/Weather/features/WeatherAlerts/utils/alertData.utils';
import {
  getLocationFromAlertPolygon,
  getLocationFromWeatherZoneIds,
} from 'features/Weather/features/WeatherAlerts/utils/alertLocation.utils';
import { SEVERE_WEATHER_ALERT_POLYGON_PROVIDERS } from '../config/severeWeatherAlerts.config';

/**
 * The severe-weather map receives raw alerts, but it renders geographic areas. This file prepares
 * those areas in two explicit groups: official weather-zone polygons and polygons included in
 * provider alert data. Components can then render and select areas without parsing alert formats.
 */

/**
 * @typedef {[number, number]} LatitudeLongitude
 * A point written in the application's `[latitude, longitude]` order.
 *
 * @typedef {object} SevereWeatherAlertArea
 * @property {string} id - Stable ID used for React keys and map selection.
 * @property {'weather-zone'|'alert-polygon'} kind - Source of the polygons represented by the area.
 * @property {string} [weatherZoneId] - Official warning-area ID when this is a weather-zone area.
 * @property {object[]} alerts - Alerts represented by this geographic area.
 * @property {object|null} primaryAlert - Alert initially shown when the area is selected.
 * @property {number} severity - Severity score used to style the area.
 * @property {Array<Array<LatitudeLongitude>>} polygons - Map-ready affected-area polygons.
 * @property {[LatitudeLongitude, LatitudeLongitude]|null} bounds - Southwest/northeast corners.
 * @property {LatitudeLongitude|null} center - Point used to focus the selected map area.
 *
 * @typedef {object} SevereWeatherAlertAreas
 * @property {SevereWeatherAlertArea[]} weatherZoneAreas - Areas from official zone polygons.
 * @property {SevereWeatherAlertArea[]} alertPolygonAreas - Areas supplied within provider alerts.
 * @property {SevereWeatherAlertArea[]} allAlertAreas - Both collections in map render order.
 */

/**
 * Returns a dependable string ID for one severe-weather alert.
 *
 * Map areas need stable React keys, and selection state needs a value that can find the same alert
 * later. Provider IDs are used whenever possible. For providers without an ID, this function
 * builds one from provider, alert type, and issue time before using the array index as a last
 * resort.
 *
 * @param {object} alert - Alert returned by an alert provider.
 * @param {number} [fallbackIndex] - Last-resort array position for unidentifiable alerts.
 * @returns {string} Stable alert ID used by map rendering and selection.
 */
export const getStableSevereWeatherAlertId = (alert, fallbackIndex) => {
  const providerId = getAlertIdentifier(alert);
  if (providerId !== null && providerId !== undefined && providerId !== '') {
    return String(providerId);
  }

  const fallbackIdParts = [
    alert?.provider,
    alert?.description ?? alert?.type,
    alert?.issue_time_utc ?? alert?.sentTime ?? alert?.created_time ?? alert?.created,
  ]
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => encodeURIComponent(String(value).trim().toLowerCase()));

  return fallbackIdParts.length
    ? `derived:${fallbackIdParts.join(':')}`
    : `alert:${fallbackIndex ?? 'unknown'}`;
};

/**
 * Returns the same stable ID used by an area's alert list.
 *
 * @param {SevereWeatherAlertArea|null|undefined} area - Prepared map area containing the alert.
 * @param {object|null|undefined} alert - Alert whose selection ID is needed.
 * @returns {string|null} Stable ID for the alert, or null.
 */
export const getSevereWeatherAlertIdWithinArea = (area, alert) => {
  if (!area || !alert) return null;

  const alertIndex = area.alerts.indexOf(alert);
  return getStableSevereWeatherAlertId(alert, alertIndex >= 0 ? alertIndex : undefined);
};

/**
 * Splits provider alert text into the sections marked by asterisks.
 *
 * NWS alert messages use `*` as a section delimiter for content such as WHAT, WHERE, WHEN, and
 * IMPACTS. Empty sections are removed so callers can render the remaining text as a semantic list
 * without exposing the provider delimiter.
 *
 * @param {unknown} alertText - Raw headline or description supplied by an alert provider.
 * @returns {string[]} Trimmed alert-text sections in their original order.
 */
export const getSevereWeatherAlertTextSections = (alertText) => {
  if (alertText === null || alertText === undefined) return [];

  return String(alertText)
    .split('*')
    .map((alertTextSection) => alertTextSection.trim())
    .filter(Boolean);
};

/**
 * Returns the concise title portion of a severe-weather alert headline.
 *
 * Provider headlines sometimes append WHAT and WHERE sections after an asterisk. Those sections
 * belong in the alert description rather than in map and panel headings, so only the text before
 * the first delimiter is returned here.
 *
 * @param {object|null|undefined} alert - Alert returned by an alert provider.
 * @returns {string} Concise severe-weather alert headline.
 */
export const getSevereWeatherAlertHeadline = (alert) => {
  const [headline] = getSevereWeatherAlertTextSections(getAlertHeadline(alert));
  return headline ?? 'No headline available';
};

/**
 * Builds one map area for each official weather zone referenced by active alerts.
 *
 * A weather-zone ID identifies a predefined warning area, such as an NWS UGC zone. Several alerts
 * may reference the same zone, so this function groups them under one map area and removes
 * duplicate alerts. The most severe alert becomes the area's default details and determines its
 * styling.
 *
 * @param {object[]} alerts - Active severe-weather alerts returned by all providers.
 * @returns {SevereWeatherAlertArea[]} One prepared map area per known weather-zone ID.
 */
const buildWeatherZoneAreas = (alerts) => {
  const areaByWeatherZoneId = new Map();

  alerts.forEach((alert, alertIndex) => {
    const alertId = getStableSevereWeatherAlertId(alert, alertIndex);

    getAlertWeatherZoneIds(alert).forEach((weatherZoneId) => {
      const location = getLocationFromWeatherZoneIds(weatherZoneId);
      if (!location) return;

      const area = areaByWeatherZoneId.get(weatherZoneId) ?? {
        id: `weather-zone:${weatherZoneId}`,
        kind: 'weather-zone',
        weatherZoneId,
        alerts: [],
        includedAlertIds: new Set(),
        polygons: location.polygons,
        bounds: location.bounds,
        center: location.center,
      };

      if (!area.includedAlertIds.has(alertId)) {
        area.includedAlertIds.add(alertId);
        area.alerts.push(alert);
      }

      areaByWeatherZoneId.set(weatherZoneId, area);
    });
  });

  return [...areaByWeatherZoneId.values()].map(({ includedAlertIds, ...area }) => {
    const primaryAlert = getHighestSeverityAlert(area.alerts);

    return {
      ...area,
      primaryAlert,
      severity: getAlertSeverity(primaryAlert) ?? 0,
    };
  });
};

/**
 * Builds one map area for each supported alert that includes its own polygon.
 *
 * Some providers send the exact affected-area outline inside each alert. This function prepares
 * those polygons separately from official weather zones, skips duplicate alert IDs, and ignores
 * malformed polygons. The provider allowlist prevents unrelated polygon formats from being
 * interpreted incorrectly.
 *
 * @param {object[]} alerts - Active severe-weather alerts returned by all providers.
 * @returns {SevereWeatherAlertArea[]} Map areas created from provider-supplied alert polygons.
 */
const buildAlertPolygonAreas = (alerts) => {
  const providersThatSupplyAlertPolygons = new Set(SEVERE_WEATHER_ALERT_POLYGON_PROVIDERS);
  const createdAreaIds = new Set();

  return alerts.flatMap((alert, alertIndex) => {
    const providerName = String(alert?.provider ?? '')
      .trim()
      .toLowerCase();
    if (!providersThatSupplyAlertPolygons.has(providerName)) return [];

    const alertId = getStableSevereWeatherAlertId(alert, alertIndex);
    const areaId = `alert-polygon:${alertId}`;
    if (createdAreaIds.has(areaId)) return [];

    const location = getLocationFromAlertPolygon(alert?.polygon);
    if (!location) return [];

    createdAreaIds.add(areaId);
    return [
      {
        id: areaId,
        kind: 'alert-polygon',
        alert,
        alerts: [alert],
        primaryAlert: alert,
        severity: getAlertSeverity(alert) ?? 0,
        polygons: location.polygons,
        bounds: location.bounds,
        center: location.center,
      },
    ];
  });
};

/**
 * Converts raw severe-weather alerts into the geographic collections rendered by the map.
 *
 * Map components call this preparation function once, then render `weatherZoneAreas` and
 * `alertPolygonAreas` without knowing provider field names or polygon lookup rules. An alert may
 * appear in both groups when it references an official zone and also supplies a more precise
 * polygon; that lets the map decide which layers to display.
 *
 * @param {object[]} alerts - Active severe-weather alerts returned by all providers.
 * @returns {SevereWeatherAlertAreas} Prepared weather-zone, alert-polygon, and combined areas.
 */
export const buildSevereWeatherAlertAreas = (alerts) => {
  if (!Array.isArray(alerts) || !alerts.length) {
    return {
      weatherZoneAreas: [],
      alertPolygonAreas: [],
      allAlertAreas: [],
    };
  }

  const weatherZoneAreas = buildWeatherZoneAreas(alerts);
  const alertPolygonAreas = buildAlertPolygonAreas(alerts);

  return {
    weatherZoneAreas,
    alertPolygonAreas,
    allAlertAreas: [...weatherZoneAreas, ...alertPolygonAreas],
  };
};

/**
 * Returns the readable name shown for one selectable map area.
 *
 * Official weather-zone polygons are named with their zone ID. Provider polygons do not have their
 * own geographic identifier, so their label uses the primary alert headline instead. Map popups,
 * the keyboard-accessible area selector, and the information panel all use this same wording.
 *
 * @param {SevereWeatherAlertArea|null|undefined} area - Prepared area to describe.
 * @returns {string} Human-readable area name.
 */
export const getSevereWeatherAlertAreaLabel = (area) => {
  if (area?.kind === 'weather-zone' && area.weatherZoneId) {
    return `Weather zone ${area.weatherZoneId}`;
  }

  if (area?.kind === 'alert-polygon') {
    return `Alert polygon: ${getSevereWeatherAlertHeadline(area.primaryAlert)}`;
  }

  return 'Unknown alert area';
};

/**
 * Finds a prepared geographic area after a user selects it on the map or in a list.
 *
 * Selection values can arrive as strings or numbers, so both sides are compared as strings. A
 * missing collection or unmatched ID returns null and keeps selection handling predictable.
 *
 * @param {SevereWeatherAlertAreas} alertAreas - Result from `buildSevereWeatherAlertAreas`.
 * @param {string|number} areaId - Selection ID of the geographic area to find.
 * @returns {SevereWeatherAlertArea|null} Matching prepared area, or null.
 */
export const findSevereWeatherAlertAreaById = (alertAreas, areaId) => {
  return alertAreas?.allAlertAreas?.find((area) => String(area.id) === String(areaId)) ?? null;
};

/**
 * Finds a raw alert after a details panel or map interaction provides its stable ID.
 *
 * This uses the same fallback-ID rules as area preparation, which lets alerts without a provider
 * ID remain selectable. Missing collections and unmatched IDs return null.
 *
 * @param {object[]} alerts - Raw active severe-weather alerts.
 * @param {string|number} alertId - Stable alert ID to find.
 * @returns {object|null} Matching raw alert, or null.
 */
export const findSevereWeatherAlertById = (alerts, alertId) => {
  return (
    alerts?.find(
      (alert, index) => getStableSevereWeatherAlertId(alert, index) === String(alertId),
    ) ?? null
  );
};
